<template>
  <div class="fld">
    <span class="fld__icon"><v-icon>mdi-clock-outline</v-icon></span>
    <div style="min-width: 0">
      <div class="d-flex align-center" style="gap: 6px">
        <span class="fld__label">Verfügbarkeiten</span>
        <span v-if="loading" class="acq-tiny">· lädt …</span>
      </div>
      <div v-if="!hasAvailabilities" class="fld__value fld__value--muted">Keine Verfügbarkeiten hinterlegt</div>
      <template v-else>
        <availability-week :availabilities="filtered"/>
        <div v-if="expanded" class="d-flex flex-column mt-2" style="gap: 8px">
          <pill-group :items="labelItems" :value="labelSelection" label="Verfügbarkeiten filtern" @input="labelSelection = $event"/>
          <v-checkbox v-if="!disableRealTime" v-model="useRealTime" class="mt-0" color="info" dense hide-details
                      label="Mit Kursen, Schulungen usw. abgleichen"/>
          <span v-if="updateDate" class="acq-tiny">Zuletzt aktualisiert am {{ updateDate }}</span>
        </div>
        <button class="more" type="button" @click="expanded = !expanded">
          {{ expanded ? 'Weniger' : `${filterText} · Filter` }}
          <v-icon x-small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </button>
      </template>
    </div>
    <div class="fld__actions">
      <button v-if="canUpdate" :class="{ 'ibtn--add': !hasAvailabilities }" class="ibtn" v-tip="'Verfügbarkeiten bearbeiten'" aria-label="Verfügbarkeiten bearbeiten" type="button"
              @click="showDialog = true">
        <v-icon>{{ hasAvailabilities ? 'mdi-pencil-outline' : 'mdi-plus' }}</v-icon>
      </button>
    </div>
    <update-institution-availabilities-dialog v-model="showDialog" :institution-id="institution._id" @update:availabilities="onUpdate"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import AvailabilityWeek from '@/components/common/profileUi/AvailabilityWeek.vue';
import moment from 'moment';
import PillGroup from '@/components/common/modern/pillGroup.vue';
import UpdateInstitutionAvailabilitiesDialog
  from '@/components/institutions/institutionView/profileView/availabilitiesListItem/updateInstitutionAvailabilitiesDialog.vue';
import {SchedulerApi} from '@/classes/api/scheduler.api.class';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {InstitutionFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/InstitutionFields';
import {AvailabilityInterface, LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {AvailabilityLabels, AvailabilityLabelsVue} from '@/enums/AvailabilityLabels';
import {Institution} from '@/interfaces/institution.interface';
import {EventBus} from '@/busses/EventBus';


/** Week strip of the availabilities with the same label filter and real-time matching as the profile */
export default Vue.extend({
  components: {AvailabilityWeek, PillGroup, UpdateInstitutionAvailabilitiesDialog},
  props: {
    institution: {type: Object as () => Institution, required: true},
  },
  data: () => ({
    loading: false,
    expanded: false,
    showDialog: false,
    labelSelection: [AvailabilityLabels.FOR_COURSES] as AvailabilityLabels[],
    useRealTime: true,
    disableRealTime: false,
    realTime: [] as LabeledAvailabilityInterface[],
  }),
  computed: {
    labelItems: () => AvailabilityLabelsVue.map((label) => ({value: label.value, text: label.text})),
    hasAvailabilities(): boolean {
      return !!this.institution.availabilities?.length;
    },
    canUpdate(): boolean {
      const entity = this.$$getCrudEntity(CrudEntityTypes.INSTITUTION, this.institution._id);
      return entity.canRead(InstitutionFields.AVAILABILITIES) && entity.canUpdate(InstitutionFields.AVAILABILITIES);
    },
    filterText(): string {
      const texts = AvailabilityLabelsVue.filter((label) => this.labelSelection.includes(label.value)).map((label) => label.text);
      return texts.length ? texts.join(', ') : 'Alle';
    },
    filtered(): LabeledAvailabilityInterface[] {
      const all = (this.useRealTime && !this.disableRealTime ? this.realTime : this.institution.availabilities || []) as LabeledAvailabilityInterface[];
      if (!this.labelSelection.length) {
        return all;
      }
      const matching = all.filter((av) => av.labels.some((label) => this.labelSelection.includes(label)));
      return this.labelSelection.includes(AvailabilityLabels.EXCEPTIONAL)
        ? matching
        : matching.filter((av) => !av.labels.includes(AvailabilityLabels.EXCEPTIONAL));
    },
    updateDate(): string {
      const stamps = (this.institution.availabilities || []).filter((av: any) => !!av.updatedAt)
        .map((av: any) => new Date(av.updatedAt as Date).valueOf());
      return stamps.length ? moment(Math.max(...stamps)).format('DD.MM.YYYY [um] HH:mm [Uhr]') : '';
    },
  },
  methods: {
    onUpdate(availabilities: AvailabilityInterface[] | null) {
      this.$set(this.institution, 'availabilities', availabilities);
      this.loadRealTime();
    },
    async loadRealTime() {
      if (!this.$$getCrudEntity(CrudEntityTypes.INSTITUTION, this.institution._id).canRead(InstitutionFields.AVAILABILITIES)) {
        this.disableRealTime = true;
        return;
      }
      this.loading = true;
      try {
        const resp = await SchedulerApi.getAvailabilities({type: CrudEntityTypes.INSTITUTION, id: this.institution._id});
        this.realTime = resp.availabilities;
        this.disableRealTime = false;
      } catch (e) {
        this.disableRealTime = true;
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    'institution._id': {
      immediate: true,
      handler() {
        this.loadRealTime();
      },
    },
  },
  mounted() {
    EventBus.$on('update:availabilities', this.loadRealTime);
  },
  beforeDestroy() {
    EventBus.$off('update:availabilities', this.loadRealTime);
  },
});
</script>

<style scoped>






.more {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12px;
  color: rgba(15, 23, 42, .55);
  cursor: pointer;
}

.more:hover {
  color: #1565C0;
}
</style>
<style src="./itemDetails.css"></style>
<style src="./acquisitionViews.css"></style>
